import { useState, useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { FigureProperties } from './FigureProperties';
import { PanelProperties } from './PanelProperties';
import { AnnotationProperties } from './AnnotationProperties';
import { WarningsStrip } from './WarningsStrip';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export function PropertiesPanel() {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const selectedPanelId = useFigureCompositionStore((state) => state.selectedPanelId);
  const selectedAnnotationId = useFigureCompositionStore((state) => state.selectedAnnotationId);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [panelTab, setPanelTab] = useState('content');
  const prevAnnotationIdRef = useRef<string | null>(null);

  // Track annotation selection to switch tabs appropriately
  useEffect(() => {
    if (selectedAnnotationId) {
      // Annotation selected - switch to annotations tab
      setPanelTab('annotations');
      prevAnnotationIdRef.current = selectedAnnotationId;
    } else if (prevAnnotationIdRef.current !== null && !selectedAnnotationId) {
      // Annotation was closed - ensure we're on annotations tab
      setPanelTab('annotations');
      prevAnnotationIdRef.current = null;
    }
  }, [selectedAnnotationId]);

  if (!currentComposition) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4">
        <p className="text-muted-foreground text-sm">No figure loaded</p>
      </div>
    );
  }

  const selectedPanel = selectedPanelId
    ? currentComposition.panels.find((p) => p.id === selectedPanelId) ?? null
    : null;
  
  const selectedAnnotation = selectedAnnotationId && selectedPanel
    ? selectedPanel.annotations?.find((a) => a.id === selectedAnnotationId) ?? null
    : null;
  
  // Clear selection if panel was removed (use effect to avoid render-time state updates)
  useEffect(() => {
    if (selectedPanelId && !selectedPanel) {
      useFigureCompositionStore.getState().setSelectedPanelId(null);
    }
  }, [selectedPanelId, selectedPanel]);

  // Clear annotation selection if annotation was removed
  useEffect(() => {
    if (selectedAnnotationId && !selectedAnnotation) {
      useFigureCompositionStore.getState().setSelectedAnnotationId(null);
    }
  }, [selectedAnnotationId, selectedAnnotation]);

  return (
    <div className="w-full h-full flex flex-col bg-card border-l border-border">
      {/* Header */}
      <div 
        className="bg-card border-b border-border h-12 px-4 flex items-center"
        data-properties-panel-header
        tabIndex={-1}
        role="region"
        aria-label={
          selectedAnnotation
            ? `Annotation Properties for ${selectedAnnotation.type} annotation on panel ${selectedPanel?.label}`
            : selectedPanel
            ? `Panel Properties for ${selectedPanel.label}`
            : 'Figure Properties'
        }
      >
        <div className="flex items-center justify-between w-full">
          <h3 className="text-xs font-semibold text-foreground">Figure editor</h3>
          <span className="text-[10px] text-muted-foreground">
            {selectedAnnotation ? (
              <>
                Editing: <span className="font-semibold text-foreground">Annotation</span> (Panel <span className="font-semibold text-foreground">{selectedPanel?.label}</span>)
              </>
            ) : selectedPanel ? (
              <>
                Editing: <span className="font-semibold text-foreground">Panel {selectedPanel.label}</span>
              </>
            ) : (
              <>
                Editing: <span className="font-semibold text-foreground">Figure</span>
              </>
            )}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Context-Aware Content */}
        <div className="flex-1 overflow-hidden">
          {selectedAnnotation && selectedPanel ? (
            <div className="h-full overflow-y-auto px-4 py-3">
              <AnnotationProperties
                panel={selectedPanel}
                annotation={selectedAnnotation}
                showAdvanced={showAdvanced}
              />
            </div>
          ) : selectedPanel ? (
            <PanelProperties 
              panel={selectedPanel} 
              showAdvanced={showAdvanced}
              activeTab={panelTab}
              onTabChange={setPanelTab}
            />
          ) : (
            <FigureProperties showAdvanced={showAdvanced} />
          )}
        </div>

        {/* Warnings Strip */}
        <div className="border-t px-4 py-2">
          <WarningsStrip />
        </div>
      </div>
    </div>
  );
}

